import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { config as configApi, normalizeError, theses as thesesApi, pdfs as pdfsApi } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Editor from "@/components/Editor";
import NumaPanel from "@/components/NumaPanel";
import DashboardView from "@/components/DashboardView";
import HomeView from "@/components/HomeView";
import HistoryView from "@/components/HistoryView";
import PDFLibrary from "@/components/PDFLibrary";
import SettingsView from "@/components/SettingsView";
import ReviewView from "@/components/ReviewView";
import StartingPointView from "@/components/StartingPointView";
import ExportView from "@/components/ExportView";
import ImportThesisModal from "@/components/ImportThesisModal";
import NewChapterModal from "@/components/NewChapterModal";
import NewThesisModal from "@/components/NewThesisModal";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Menu, PanelLeftOpen, PanelRightOpen, ScrollText } from "lucide-react";
import NumaLogo from "@/components/NumaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";

// Every key the sidebar can send. A key missing from this list is not an
// error anywhere: navView simply ignores it, and the section becomes
// unreachable with the button still looking perfectly clickable - which is
// exactly what happened to "checks".
// Sections that a manuscript brought in for review has no use for.
const OFF_IN_REVIEW = ["start", "dashboard"];

const VIEW_KEYS = [
  "home", "editor", "sources", "start", "review", "dashboard", "history", "export",
  "settings",
];

export default function Workspace() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [thesis, setThesis] = useState(null);
  const [theses, setTheses] = useState([]);
  /*
    Se l'elenco dei progetti è ancora per strada.

    Finché non si sa, non si può dire «non hai progetti»: ricaricando la pagina
    quella frase compariva per primi - ed è la frase che fa pensare di aver
    perso il lavoro di settimane.
  */
  const [caricandoProgetti, setCaricandoProgetti] = useState(true);
  // How many projects this deployment allows. Asked of the server, because the
  // server is what enforces it: a number compiled into the page would go stale
  // the day the limit changes.
  const [maxProjects, setMaxProjects] = useState(null);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [view, setView] = useState("home");
  const [importOpen, setImportOpen] = useState(false);
  // The passage the editor should scroll to and highlight, if any.
  const [passage, setPassage] = useState(null);
  // An AI reading asked for from the review page, run by the panel.
  const [aiRequest, setAiRequest] = useState(null);
  const [newThesisOpen, setNewThesisOpen] = useState(false);
  const [newChapterOpen, setNewChapterOpen] = useState(false);
  const [pdfList, setPdfList] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startingPrompt, setStartingPrompt] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // thesis object
  // Mostrato quando l'account è pieno: un limite va spiegato, non annunciato
  // con «operazione non riuscita».
  const [limitOpen, setLimitOpen] = useState(false);

  // Both side panels can be folded away: on a laptop they leave the writing
  // column too narrow to work in. The choice is remembered per browser.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("numa_sidebar_collapsed") === "1"
  );

  useEffect(() => {
    localStorage.setItem("numa_sidebar_collapsed", sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  // Ctrl/Cmd + \ toggles the navigation, the shortcut most editors use.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        setSidebarCollapsed((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  const refreshTheses = async () => {
    const list = await thesesApi.list();
    setTheses(list);
    return list;
  };

  /*
    Aprire un progetto: prima l'elenco, poi il testo di quello scelto.

    L'elenco arriva senza il contenuto dei capitoli - su una tesi importata
    sono megabyte - quindi quello che si apre va richiesto per intero. Se la
    richiesta non riesce si tiene la scheda leggera: i titoli ci sono, e
    l'editor dirà che il testo non è arrivato, invece di una pagina vuota.
  */
  const apriProgetto = async (leggero) => {
    if (!leggero?.thesis_id) { setThesis(null); return null; }
    setThesis(leggero);
    setActiveChapterId(leggero.chapters?.[0]?.id || null);
    try {
      const intero = await thesesApi.get(leggero.thesis_id);
      setThesis(intero);
      return intero;
    } catch {
      return leggero;
    }
  };

  /*
    Che cosa aprire, all'arrivo.

    L'indirizzo può dire tutto: quale progetto, quale sezione, quale passaggio -
    è così che «Vedi nel testo» apre l'editor in una seconda scheda. Si legge
    qui, dentro lo stesso passaggio che carica i progetti, e non in un effetto a
    parte: erano due che si rincorrevano, e vinceva quello che finiva per
    ultimo. Il risultato era una scheda che si apriva sul primo capitolo invece
    che sul punto chiesto.
  */
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const list = await refreshTheses();
        const parametri = new URLSearchParams(window.location.search);
        const chiesto = parametri.get("progetto");
        // Nessun progetto creato d'ufficio a chi arriva per la prima volta.
        const scelto = (chiesto && list.find((t) => t.thesis_id === chiesto)) || list[0] || null;
        await apriProgetto(scelto);

        if (chiesto && scelto?.thesis_id === chiesto) {
          const sezione = parametri.get("sezione");
          if (sezione) setActiveChapterId(sezione);
          setPassage({
            chapterId: sezione || undefined,
            excerpt: parametri.get("cerca") || "",
            position: parametri.get("da") != null ? Number(parametri.get("da")) : null,
            at: Date.now(),
          });
          setView("editor");
        }
        if (chiesto) {
          // L'indirizzo ha fatto il suo lavoro: sparisce dalla barra, così un
          // aggiornamento della pagina non riporta ogni volta allo stesso punto.
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch (e) {
        toast.error("Impossibile caricare i progetti.");
      } finally {
        setCaricandoProgetti(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load PDF list whenever thesis changes so the activity feed can include PDF events
  useEffect(() => {
    if (!thesis?.thesis_id) return;
    pdfsApi.list(thesis.thesis_id).then(setPdfList).catch(() => {});
  }, [thesis?.thesis_id]);

  const reviewMode = thesis?.project_mode === "review";

  /**
   * Open the editor at the passage a finding is about.
   *
   * "Periodo di 67 parole" is not actionable until the author can see which
   * period: the point of a review is to be worked through, and hunting for
   * each item in a long document is most of the work.
   */
  const locateFinding = (finding) => {
    if (finding?.chapter_id) setActiveChapterId(finding.chapter_id);
    setPassage({
      chapterId: finding?.chapter_id,
      excerpt: finding?.excerpt || "",
      position: finding?.position ?? null,
      // Distinguishes two requests for the same passage, so the editor scrolls
      // again when the author clicks the same finding twice.
      at: Date.now(),
    });
    setView("editor");
  };
  const activeChapter = thesis?.chapters?.find((c) => c.id === activeChapterId) || null;

  // Opening a project that exists to be reviewed lands on the review.
  useEffect(() => {
    if (reviewMode && OFF_IN_REVIEW.includes(view)) setView("review");
  }, [reviewMode, view]);

  const onChapterUpdate = (updated) => {
    setThesis(updated);
    setTheses((prev) => prev.map((t) => (t.thesis_id === updated.thesis_id ? updated : t)));
  };

  useEffect(() => {
    configApi.get().then((c) => setMaxProjects(c.max_projects)).catch(() => setMaxProjects(null));
  }, []);

  /*
    A full account is told before it fills a form, not after.

    The server refuses either way - that is where the limit lives - but a
    dialog that takes a title, a type and a file only to answer "no" wastes the
    work it just asked for.
  */
  const atProjectLimit = maxProjects !== null && theses.length >= maxProjects;
  const refuseNewProject = () => setLimitOpen(true);
  const requestNewThesis = () => {
    if (atProjectLimit) return refuseNewProject();
    setNewThesisOpen(true);
  };
  const requestImport = () => {
    if (atProjectLimit) return refuseNewProject();
    setImportOpen(true);
  };

  // A section belongs to a project; without one there is nothing to add it to,
  // so the action explains itself instead of opening an unusable dialog.
  const requestNewChapter = () => {
    if (!thesis?.thesis_id) {
      toast.error("Nessun progetto aperto", {
        description: "Crea o apri un progetto prima di aggiungere una sezione.",
        action: { label: "Nuovo progetto", onClick: requestNewThesis },
      });
      return;
    }
    setNewChapterOpen(true);
  };

  const navView = (key) => {
    // The sidebar already refuses these, but a card on the overview or a link
    // in a message must not be able to route somewhere the mode has closed.
    if (OFF_IN_REVIEW.includes(key) && reviewMode) return;
    if (key === "checks") return setView("review");   // vecchio nome, stessa pagina
    if (VIEW_KEYS.includes(key)) setView(key);
    if (key === "start") setStartingPrompt("");
    setSidebarOpen(false);
  };

  const openStartingPoint = (prompt = "") => {
    setStartingPrompt(prompt);
    setView("start");
  };

  // Home action -> route to the right view / open modal
  const handleHomeAction = (key) => {
    if (key === "writing") setView("editor");
    // «review» è la scheda dei controlli rapidi, «deep-review» la rilettura
    // completa: due pagine diverse, e in stesura servono tutt'e due.
    else if (key === "review" || key === "deep-review") setView("review");
    else if (key === "deep-review") setView("review");
    else if (key === "sources") setView("sources");
    else if (key === "stats") setView("dashboard");
    else if (key === "plan") setView("editor");
    else if (key === "start") openStartingPoint();
    else if (key === "history") setView("history");
    else if (key === "export") setView("export");
    else if (key === "create") requestNewThesis();
    else if (key === "import") requestImport();
  };

  const handleThesisSelect = (t) => {
    apriProgetto(t);
    setView("home");
    setSidebarOpen(false);
  };

  const handleThesisCreate = async (payload = { title: "Nuovo documento", subtitle: "", project_type: "thesis" }) => {
    try {
      const newT = await thesesApi.create(payload);
      await refreshTheses();
      handleThesisSelect(newT);
      toast.success("Progetto creato.");
    } catch (e) {
      /*
        The server says why. Swallowing that and printing "creazione non
        riuscita" is how a full account became a mystery: the answer was in the
        response all along.
      */
      const err = normalizeError(e);
      if (err.code === "project_limit") {
        setLimitOpen(true);
        await refreshTheses();       // il conteggio mostrato torna esatto
      } else {
        toast.error("Creazione del progetto non riuscita", { description: err.message });
      }
      throw new Error("create failed");
    }
  };

  const handleThesisDelete = (t) => setConfirmDelete(t);

  const doDelete = async () => {
    const t = confirmDelete;
    if (!t) return;
    try {
      await thesesApi.remove(t.thesis_id);
      const list = await refreshTheses();
      toast.success(`"${t.title}" eliminato.`);
      if (thesis?.thesis_id === t.thesis_id) {
        const next = list[0];
        if (next) handleThesisSelect(next);
        else setThesis(null);
      }
    } catch {
      toast.error("Eliminazione non riuscita.");
    } finally {
      setConfirmDelete(null);
    }
  };

  const activities = useMemo(() => {
    const items = [];
    thesis?.chapters?.forEach((c) => {
      if (c.content && c.content.length > 20) {
        items.push({ kind: "edit", title: `Modificata «${c.title}»`, when: `${c.progress}% completato` });
      }
    });
    pdfList.slice(0, 3).forEach((p) => {
      items.push({ kind: p.summary ? "summary" : "import", title: p.summary ? `Riassunto pronto · ${p.filename}` : `Caricato ${p.filename}`, when: `${p.pages} pagine` });
    });
    return items.slice(0, 6);
  }, [thesis, pdfList]);

  if (authLoading) {
    // Non si arriva quasi mai qui - ProtectedRoute mostra già l'avvio - ma se
    // ci si arriva vale la stessa regola: un logo muto sembra un guasto.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper"
           data-testid="workspace-loading">
        <NumaLogo size={9} className="numa-mark animate-pulse" variant={0} />
        <p className="text-[13.5px] text-ink-muted">Sto aprendo il tuo lavoro…</p>
      </div>
    );
  }

  const sidebarNav = (collapsible) => (
    <Sidebar
      thesis={thesis}
      theses={theses}
      onThesisSelect={handleThesisSelect}
      onThesisCreate={requestNewThesis}
      onThesisDelete={handleThesisDelete}
      activeChapterId={activeChapterId}
      onChapterSelect={(id) => { setActiveChapterId(id); setView("editor"); setSidebarOpen(false); }}
      view={view}
      onViewChange={navView}
      onImport={requestImport}
      onNewChapter={requestNewChapter}
      // No "hide" inside the drawer: it is dismissed by its own close button.
      onCollapse={collapsible ? () => setSidebarCollapsed(true) : undefined}
    />
  );

  // Mounted once, with no chrome of its own: it runs the reading asked for from
  // the review or the editor and shows the answer.
  const numaEngine = (
    <NumaPanel
      request={aiRequest}
      chapter={activeChapter}
      thesis={thesis}
      onChapterUpdate={onChapterUpdate}
    />
  );

  return (
    <div className="h-screen overflow-hidden bg-paper" data-testid="workspace">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-surface px-3 lg:hidden">
        <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
          <Menu className="h-5 w-5 text-ink-muted" />
          <span className="sr-only">Apri il menu</span>
        </Button>
        <div className="flex items-center gap-2">
          <NumaLogo size={6} className="numa-mark" variant={0} />
          <span className="font-serif-display font-semibold text-ink">NUMA</span>
          <ThemeToggle className="ml-1" />
        </div>
      </div>

      <div
        className={`h-[calc(100vh-3.5rem)] lg:h-screen lg:grid ${
          sidebarCollapsed
            ? "lg:grid-cols-[1fr]"
            : "lg:grid-cols-[280px_1fr]"
        }`}
      >
        {!sidebarCollapsed && <div className="hidden lg:block">{sidebarNav(true)}</div>}

        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-[290px] lg:hidden">
            {sidebarNav(false)}
          </SheetContent>
        </Sheet>

        <main className="relative overflow-hidden">
          {sidebarCollapsed && (
            <button
              type="button"
              data-testid="expand-sidebar"
              onClick={() => setSidebarCollapsed(false)}
              title="Mostra il pannello di navigazione (Ctrl+\\)"
              className="absolute left-3 top-3 z-30 hidden items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 py-1.5 text-2xs text-ink-muted shadow-raised transition-colors hover:bg-surface-2 hover:text-ink lg:inline-flex"
            >
              <PanelLeftOpen className="h-3.5 w-3.5" /> Menu
            </button>
          )}
          {view === "home" && (
            <HomeView
              thesis={thesis}
              caricando={caricandoProgetti}
              activities={activities}
              onAction={handleHomeAction}
              onOpenProject={() => setView("editor")}
            />
          )}
          {view === "editor" && (
            <Editor
              thesis={thesis}
              chapter={activeChapter}
              onChapterUpdate={onChapterUpdate}
              onNeedHelp={openStartingPoint}
              passage={passage}
              onChapterFocus={setActiveChapterId}
              onRunAi={(key) => setAiRequest({ key, at: Date.now() })}
            />
          )}
          {view === "dashboard" && <DashboardView thesis={thesis} pdfs={pdfList} />}
          {view === "review" && (
            <ReviewView
              thesis={thesis}
              onChapterUpdate={setThesis}
              onLocate={locateFinding}
              onRunAi={(key) => setAiRequest({ key, at: Date.now() })}
            />
          )}
          {view === "sources" && <PDFLibrary thesis={thesis} onListChange={setPdfList} />}
          {view === "start" && <StartingPointView thesis={thesis} pdfs={pdfList} initialPrompt={startingPrompt} />}
          {view === "history" && <HistoryView thesis={thesis} onChapterUpdate={onChapterUpdate} />}
          {view === "export" && <ExportView thesis={thesis} />}
          {view === "settings" && <SettingsView thesis={thesis} />}
        </main>
      </div>

      {numaEngine}

      <ImportThesisModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onProjectLimit={async () => { await refreshTheses(); setLimitOpen(true); }}
        onImported={async (t) => {
          await refreshTheses();
          setThesis(t);                       // arriva già intero da chi lo ha creato
          setActiveChapterId(t.chapters?.[0]?.id);
          setView("editor");
        }}
      />

      <NewThesisModal
        open={newThesisOpen}
        onOpenChange={setNewThesisOpen}
        onCreate={handleThesisCreate}
        onImportInstead={() => setImportOpen(true)}
      />

      <NewChapterModal
        open={newChapterOpen}
        onOpenChange={setNewChapterOpen}
        thesis={thesis}
        onChapterCreated={(updatedThesis) => {
          setThesis(updatedThesis);
          if (updatedThesis.chapters?.length > 0) {
            setActiveChapterId(updatedThesis.chapters[updatedThesis.chapters.length - 1].id);
            setView("editor");
          }
        }}
      />

      <AlertDialog open={limitOpen} onOpenChange={setLimitOpen}>
        <AlertDialogContent data-testid="project-limit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display">
              Puoi creare massimo {maxProjects ?? 5} progetti per account
            </AlertDialogTitle>
            <AlertDialogDescription>
              Ne hai {theses.length} su {maxProjects ?? 5}. <br />Per crearne uno nuovo, elimina un
              progetto che non ti serve più. <br />Se vuoi conservarlo, <strong className="text-ink">esportalo
              prima</strong> in DOCX o PDF da <em>Esporta</em> — l&apos;eliminazione cancella anche
              le fonti caricate e lo storico delle versioni.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="project-limit-ok">Ho capito</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-thesis-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display">Eliminare questo progetto?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <strong className="text-ink">{confirmDelete?.title}</strong>.
              Verranno cancellati anche le fonti caricate e lo storico delle versioni.
              L&apos;operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-thesis"
              onClick={doDelete}
              className="bg-danger text-on-solid hover:bg-danger/90"
            >
              Elimina progetto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
